import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import useApi from "../../hooks/useApi";
import { useTranslation } from "react-i18next";
import { registerUser, loginUser, resetEmail } from "../../api/FlaskAPI";
import { toast } from "react-toastify";
import * as constants from "../../utils/constants";
import { validateEmail } from "../../utils/validations";
import { useDispatch } from "react-redux";
import { setUser } from "../../reducers/userSlice";
import Loading from "../Buttons/Loading";
import Select from 'react-select';

/**
 * wait a little bit then,
 * Login: close modal , registeration: switch to login
 * navigate to indexes screen if in home
 */
const OnSignSuccess = (navigate, cbFunc) => {
  setTimeout(() => {
    //either: close modal, or switch to login
    cbFunc();
    //check user is in home
    //navigate to vci
  }, 1000);
};
/**
 *
 *
 * @returns registeration form component
 */
export function RegisterationForm({ setShowModal, switchToLogin }) {
  const dispatch = useDispatch();
  const [formData, setFormData] = useState({
    email: "",
    username: "",
    password: "",
    confirmPassword: "",
    first_name: "",
    middle_name: "",
    last_name: "",
    organization: "",
    fields: [],
    errors: {},
  });

  // State for general error message
  const [generalError, setGeneralError] = useState("");

  const agriculturalFields = [
    "Agriculture",
    "Disaster Management",
    "Climate Monitoring",
    "Urban Planning",
    "Weather Forecasting",
    "Forest Mapping",
    "Water Management"
  ];

  const registerRequest = useApi(registerUser);
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const isArabic = i18n.language === 'ar';
  const [processing, setProcessing] = useState(false);

  const handleRegisteration = async () => {
    const {
      email,
      password,
      username,
      confirmPassword,
      first_name,
      middle_name,
      last_name,
      organization,
      fields
    } = formData;

    // Check if required fields are filled
    if (
      !email ||
      !password ||
      !username ||
      !confirmPassword ||
      !first_name ||
      !last_name ||
      !organization
      // !fields
    ) {
      setFormData({
        ...formData,
        errors: {
          username: !username ? t("Sign.username_required") : "",
          email: !email ? t("Sign.email_required") : "",
          password: !password ? t("Sign.password_required") : "",
          confirmPassword: !confirmPassword ? t("Sign.confirm_required") : "",
          first_name: !first_name ? t("Sign.first_name_required") : "",
          last_name: !last_name ? t("Sign.last_name_required") : "",
          organization: !organization ? t("Sign.organization_required") : "",
          // fields: !fields ? t("Sign.field_required") : "",
        },
      });
      return;
    }

    //remove previous error when all pass
    setFormData({
      ...formData,
      errors: {},
    });
    //check email is valid
    if (!validateEmail(email)) {
      setFormData({
        ...formData,
        errors: {
          email: t("Sign.email_not_valid"),
        },
      });
      return;
    }

    //check passwords are a match
    if (password !== confirmPassword) {
      setFormData({
        ...formData,
        errors: {
          confirmPassword: t("Sign.passwords_match_required"),
        },
      });
      return;
    }

    //begin registeration
    setProcessing(true);
    const res = await registerRequest.request(formData, false);
    //connection issue
    if (!res) {
      toast.error(t("request_error"));
      setProcessing(false);
      return;
    }

    if (res.status !== 201) {
      setProcessing(false);

      // Clear previous general error
      setGeneralError("");

      // check message_ar first for Arabic
      let errorMessage;
      if (i18n.language === "ar" && res.data?.message_ar) {
        errorMessage = res.data.message_ar;
      } else if (res.data?.message) {
        errorMessage = res.data.message;
      } else {
        errorMessage = t("Sign.registeration_failed");
      }

      setGeneralError(errorMessage);
      toast.error(errorMessage);

      // If it's a username or email exists error, navigate to first tab
      if (res.data?.code === "USERNAME_EXISTS" || res.data?.code === "EMAIL_EXISTS") {
        setCurrentTab(0);
      }

      return;
    }
    //success
    toast.success(t("Sign.registeration_success"));
    setProcessing(false);
    OnSignSuccess(navigate, () => {
      switchToLogin();
      //erase register state
      setFormData({
        email: "",
        password: "",
        username: "",
        confirmPassword: "",
        errors: {},
      });
    });
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    let errors = { ...formData.errors };

    if (e.target.name === 'fields') {
      const selectedOptions = Array.from(e.target.selectedOptions, option => option.value);
      setFormData({
        ...formData,
        [e.target.name]: selectedOptions,
        errors
      });
      return;
    }

    // Real-time validation
    switch (name) {
      case 'email':
        if (!value) {
          errors.email = t("Sign.email_required");
        } else if (!validateEmail(value)) {
          errors.email = t("Sign.email_not_valid");
        } else {
          delete errors.email;
        }
        break;
      case 'username':
        if (!value) {
          errors.username = t("Sign.username_required");
        } else {
          delete errors.username;
        }
        break;
      case 'password':
        if (!value) {
          errors.password = t("Sign.password_required");
        } else {
          delete errors.password;
        }
        // Check password confirmation match if both exist
        if (formData.confirmPassword && value !== formData.confirmPassword) {
          errors.confirmPassword = t("Sign.passwords_match_required");
        } else if (formData.confirmPassword) {
          delete errors.confirmPassword;
        }
        break;
      case 'confirmPassword':
        if (!value) {
          errors.confirmPassword = t("Sign.confirm_required");
        } else if (formData.password && value !== formData.password) {
          errors.confirmPassword = t("Sign.passwords_match_required");
        } else {
          delete errors.confirmPassword;
        }
        break;
      case 'first_name':
      case 'last_name':
      case 'organization':
        if (!value) {
          errors[name] = t(`Sign.${name}_required`);
        } else {
          delete errors[name];
        }
        break;
      default:
        break;
    }

    setFormData({
      ...formData,
      [name]: value,
      errors
    });
  };

  const [currentTab, setCurrentTab] = useState(0);

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  // Function to check if there are any errors in the current tab
  const hasErrorsInCurrentTab = () => {
    const { errors } = formData;
    if (!errors) return false;

    // Check for errors in tab 0 (account info)
    if (currentTab === 0) {
      return !!(errors.email || errors.username || errors.password || errors.confirmPassword);
    }

    // Check for errors in tab 1 (personal info)
    if (currentTab === 1) {
      return !!(errors.first_name || errors.last_name || errors.organization || errors.fields);
    }

    return false;
  };

  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return (
    <div className="form">
      <h2 className="modal h2">{t("Sign.create_account")}</h2>
      <span>{t("Sign.free")}</span>

      {/* Display general error message */}
      {generalError && (
        <div className="wrong-credentials" style={{ marginTop: "10px", marginBottom: "10px", color: "red" }}>
          {generalError}
        </div>
      )}

      {/* <Box sx={{ width: '90%', bgcolor: '#48789D', borderRadius: '10px', mb: 2 }}>
  <Tabs 
    value={currentTab} 
    sx={tabStyles}
    TabIndicatorProps={{ style: { display: 'none' } }}
  >
    <Tab 
      label="SIGN.ACCOUNT_INFO" 
      disabled
      sx={{ 
        flex: 1,
        borderRight: '1px solid rgba(255, 255, 255, 0.2)',
        bgcolor: currentTab === 0 ? '#48789D' : 'rgba(72, 120, 157, 0.7)'
      }} 
    />
    <Tab 
      label="SIGN.PERSONAL_INFO" 
      disabled
      sx={{ 
        flex: 1,
        borderRight: '1px solid rgba(255, 255, 255, 0.2)',
        bgcolor: currentTab === 1 ? '#48789D' : 'rgba(72, 120, 157, 0.7)'
      }} 
    />
    <Tab 
      label="SIGN.ADDITIONAL_INFO" 
      disabled
      sx={{ 
        flex: 1,
        bgcolor: currentTab === 2 ? '#48789D' : 'rgba(72, 120, 157, 0.7)'
      }} 
    />
  </Tabs>
</Box> */}

      {currentTab === 0 && (
        <div>
          <SignInput
            name="email"
            onChange={onChange}
            value={formData.email}
            error={formData.errors?.email}
            placeholder={t("Sign.email")}
          />
          <SignInput
            name="username"
            value={formData.username}
            error={formData.errors?.username}
            onChange={onChange}
            placeholder={t("Sign.username")}
          />
          <SignInput
            name="password"
            onChange={onChange}
            type="password"
            value={formData.password}
            error={formData.errors?.password}
            placeholder={t("Sign.password")}
          />
          <SignInput
            name="confirmPassword"
            onChange={onChange}
            type="password"
            value={formData.confirmPassword}
            error={formData.errors?.confirmPassword}
            placeholder={t("Sign.confirm_password")}
          />
        </div>
      )}

      {currentTab === 1 && (
        <div>
          <SignInput
            name="first_name"
            value={formData.first_name}
            error={formData.errors?.first_name}
            onChange={onChange}
            placeholder={t("Sign.first_name")}
          />
          <SignInput
            name="middle_name"
            value={formData.middle_name}
            error={formData.errors?.middle_name}
            onChange={onChange}
            placeholder={t("Sign.middle_name")}
          />
          <SignInput
            name="last_name"
            value={formData.last_name}
            error={formData.errors?.last_name}
            onChange={onChange}
            placeholder={t("Sign.last_name")}
          />
          <SignInput
            name="organization"
            value={formData.organization}
            error={formData.errors?.organization}
            onChange={onChange}
            placeholder={t("Sign.organization")}
          />
          <div>
            <Select
              isMulti
              name="fields"
              value={formData?.fields?.map(f => ({ value: f, label: f }))}
              onChange={(selectedOptions) => {
                setFormData({
                  ...formData,
                  fields: selectedOptions ? selectedOptions.map(option => option.value) : []
                });
              }}
              options={agriculturalFields.map(fields => ({
                value: fields,
                label: fields
              }))}
              placeholder={t("Sign.select_field")}
              styles={{
                control: (base, state) => ({
                  ...base,
                  border: formData.errors?.fields
                    ? '1px solid var(--error-color)'
                    : '1px solid #d1d5db',
                  borderRadius: '0.375rem',
                  textAlign: "left",
                  backgroundColor: '#EEEEEE',
                  height: '55px'
                }),
                menu: (base) => ({
                  ...base,
                  bottom: '100%',
                  top: 'auto',
                  marginBottom: '5px'
                })
              }}
            />
            {formData.errors?.fields && (
              <div className="sign-up-pop-up-input-error">
                {formData.errors.fields}
              </div>
            )}
          </div>
        </div>
      )}

      {/* {currentTab === 2 && (
        <div>
          <div>
            <select
              name="country"
              value={formData.country}
              onChange={onChange}
              style={{
                width: "100%",
                padding: "0.5rem",
                border: formData.errors?.country
                  ? "var(--error-color) solid 1px"
                  : "1px solid #d1d5db",
                borderRadius: "0.375rem",
                fontSize: "0.875rem",
              }}
            >
              <option value="">{t("Sign.select_country")}</option>
              {countries.map((country) => (
                <option key={country} value={country}>
                  {country}
                </option>
              ))}
            </select>
            {formData.errors?.country && (
              <div className="sign-up-pop-up-input-error">
                {formData.errors.country}
              </div>
            )}
          </div>
          <SignInput
            name="position"
            value={formData.position}
            error={formData.errors?.position}
            onChange={onChange}
            placeholder={t("Sign.position")}
          />
        </div>
      )} */}

      {registerRequest.error && (
        <div className="wrong-credentials">
          {t("Sign.registeration_failed")}
        </div>
      )}
      <div
        style={{
          marginTop: "1em",
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          gap: "10px",
          width: window.innerWidth <= 680 ? "320px" : "50%"
        }}
      >
        {!processing && <button
          onClick={() => setCurrentTab((prev) => Math.max(0, prev - 1))}
          disabled={currentTab === 0}
          style={{
            flex: window.innerWidth <= 680 ? "none" : 1,
            padding: "10px",
            backgroundColor: "#48789D",
            color: "white",
            border: "none",
            borderRadius: "5px",
            cursor: currentTab === 0 ? "not-allowed" : "pointer",
            opacity: currentTab === 0 ? 0.7 : 1,
            minWidth: "auto",
            maxWidth: window.innerWidth <= 680 ? "50%" : "",
            marginBottom: window.innerWidth <= 680 ? "10px" : 0
          }}
        >
          {t("Sign.previous")}
        </button>}

        {currentTab === 1 ? (
          registerRequest.loading ? (
            <Loading />
          ) : (
            <button
              onClick={handleRegisteration}
              disabled={hasErrorsInCurrentTab()}
              style={{
                flex: window.innerWidth <= 680 ? "none" : 1,
                padding: "10px",
                backgroundColor: "#48789D",
                color: "white",
                border: "none",
                borderRadius: "5px",
                cursor: hasErrorsInCurrentTab() ? "not-allowed" : "pointer",
                opacity: hasErrorsInCurrentTab() ? 0.7 : 1,
                minWidth: "auto",
                maxWidth: window.innerWidth <= 680 ? "50%" : "",
              }}
            >
              {t("Sign.sign_up")}
            </button>
          )
        ) : (
          <button
            onClick={() => setCurrentTab((prev) => Math.min(2, prev + 1))}
            disabled={hasErrorsInCurrentTab()}
            style={{
              flex: window.innerWidth <= 680 ? "none" : 1,
              padding: "10px",
              backgroundColor: "#48789D",
              color: "white",
              border: "none",
              borderRadius: "5px",
              cursor: hasErrorsInCurrentTab() ? "not-allowed" : "pointer",
              opacity: hasErrorsInCurrentTab() ? 0.7 : 1,
              minWidth: "auto",
              maxWidth: window.innerWidth <= 680 ? "50%" : "",
            }}
          >
            {t("Sign.next")}
          </button>
        )}
      </div>
    </div>
  );
}

/**
 *
 * @param {} setformType toggle the visible form, either login or pass reset
 * @returns login form component
 */
export function LoginForm({ setformType, setShowModal }) {
  const [inactiveDialog, setInactiveDialog] = useState(false);
  const { t, i18n } = useTranslation();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    username: "",
    errors: {},
  });
  const loginRequest = useApi(loginUser);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const handleLogin = async () => {
    const { username, password } = formData;
    // check inputs aren't empty
    if (!username || !password) {
      setFormData({
        ...formData,
        errors: {
          username: !username ? t("Sign.username_required") : "",
          password: !password ? t("Sign.password_required") : "",
        },
      });
      return;
    }
    //remove previous error when all pass
    setFormData({
      ...formData,
      errors: {},
    });

    //start login call
    const res = await loginRequest.request({ username, password }, false);
    console.log('res.data', res.data);

    if (!res) {
      toast.error(t("request_error"));
      return;
    }
    console.log('res.data', res.data);

    // Check if account is inactive
    if (res.data?.active === false) {
      setInactiveDialog(true);
      return;
    }
    if (res.status !== 200) {
      let errorMessage;
      console.log('res.data', res.data);
      if (i18n.language === "ar" && res.data?.message_ar) {
        errorMessage = res.data.message_ar;
      } else if (res.data?.message) {
        errorMessage = res.data.message;
      } else {
        errorMessage = t("Sign.login_failed");
      }
      toast.error(errorMessage);
      return;
    }
    //success
    toast.success(t("Sign.login_success"));

    // Save the user data in the browser's local storage
    const user = {
      id: res.data.id,
      username: res.data.username,
      first_name: res.data.first_name,
      middle_name: res.data.middle_name,
      last_name: res.data.last_name,
      email: res.data.email,
      organization: res.data.organization,
      position: res.data.position,
      fields: res.data.fields,
      country: res.data.country,
      jwt: res.data.access_token,
      refreshToken: res.data.refresh_token,
      role: res.data.access,
    };
    localStorage.setItem(constants.USER, JSON.stringify(user));
    //
    dispatch(setUser(user));
    // to profile
    setformType(constants.USER_PROFILE);
    OnSignSuccess(navigate, () => navigate("/"));
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    let errors = { ...formData.errors };

    // Real-time validation for login fields
    switch (name) {
      case 'username':
        if (!value) {
          errors.username = t("Sign.username_required");
        } else {
          delete errors.username;
        }
        break;
      case 'password':
        if (!value) {
          errors.password = t("Sign.password_required");
        } else {
          delete errors.password;
        }
        break;
      default:
        break;
    }

    setFormData({
      ...formData,
      [name]: value,
      errors
    });
  };
  return (
    <div className="form">
      <h2 className="modal h2">{t("Sign.sign_in")}</h2>

      <span>{t("Sign.full_access")}</span>

      <SignInput
        name="username"
        value={formData.username}
        error={formData.errors?.username}
        onChange={onChange}
        placeholder={t("Sign.username")}
      />

      <SignInput
        name="password"
        onChange={onChange}
        value={formData.password}
        error={formData.errors?.password}
        type="password"
        className="sign-up-pop-up-input"
        placeholder={t("Sign.password")}
      />
      <a onClick={() => setformType(constants.PASS_RESET)}>
        {t("Sign.forgot")}
      </a>
      {loginRequest.error && (
        <div className="wrong-credentials">{t("Sign.wrong_credentials")}</div>
      )}
      {loginRequest.loading ? (
        <Loading />
      ) : (
        <button style={{ marginTop: "1em" }} onClick={handleLogin}>
          {t("Sign.sign_in")}
        </button>
      )}
      {inactiveDialog && (
        <div className="dialog-overlay" style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          zIndex: 1000
        }}>
          <div className="dialog" style={{
            backgroundColor: 'white',
            padding: '1.5rem',
            borderRadius: '0.5rem',
            maxWidth: '90%',
            width: '400px',
            textAlign: 'center'
          }}>
            <h3>{t("Sign.account_inactive")}</h3>
            <p>{t("Sign.contact_admin")}</p>
            <div className="dialog-actions">
              <button
                onClick={() => setInactiveDialog(false)}
                className="btn btn-primary"
              >
                {t("Sign.ok")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 *
 * @param {} setformType toggle the visible form, either login or pass reset
 * @returns pass reset form component
 */
export function ResetForm({ setformType }) {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: "",
  });
  const resetEmailRequest = useApi(resetEmail);
  const navigate = useNavigate();

  const sendResetEmail = async () => {
    const { email } = formData;
    // check inputs aren't empty
    if (!email) {
      setFormData({
        ...formData,
        errors: {
          email: !email ? t("Sign.email_required") : "",
        },
      });
      return;
    }
    //check email is valid
    if (!validateEmail(email)) {
      setFormData({
        ...formData,
        errors: {
          email: t("Sign.email_not_valid"),
        },
      });
      return;
    }
    //remove previous error when all pass
    setFormData({
      ...formData,
      errors: {},
    });

    //send email to user
    const res = await resetEmailRequest.request({ email }, false);
    //console.log(res)
    switch (res?.status) {
      case 200:
        toast.success(t("Sign.email_sent"));
        break;
      case 404:
        //user not found
        toast.warning(t("Sign.user_not_found"));
        break;
      default:
        toast.error(t("request_error"));
        break;
    }
  };

  const onChange = (e) => {
    const { name, value } = e.target;
    let errors = { ...formData.errors };

    // Real-time validation for email field
    if (name === 'email') {
      if (!value) {
        errors.email = t("Sign.email_required");
      } else if (!validateEmail(value)) {
        errors.email = t("Sign.email_not_valid");
      } else {
        delete errors.email;
      }
    }

    setFormData({
      ...formData,
      [name]: value,
      errors
    });
  };
  return (
    <div className="form">
      <h2 className="modal h2">{t("Sign.reset_password")}</h2>

      <span>{t("Sign.enter_email")}</span>

      <SignInput
        name="email"
        value={formData.email}
        error={formData.errors?.email}
        onChange={onChange}
        placeholder={t("Sign.email")}
      />

      {/* {loginRequest.error && (
        <div className="wrong-credentials">{t("Sign.wrong_credentials")}</div>
      )} */}
      {resetEmailRequest.loading ? (
        <Loading />
      ) : (
        <button style={{ marginTop: "1em" }} onClick={sendResetEmail}>
          {t("Sign.send_email")}
        </button>
      )}

      <a onClick={() => setformType(constants.LOGIN)}>Cancel</a>
    </div>
  );
}

/**
 *
 * @returns Sign Form input component
 */
export const SignInput = ({
  name,
  value,
  onChange,
  type,
  placeholder,
  error,
}) => {
  return (
    <div>
      <input
        name={name}
        value={value}
        onChange={onChange}
        type={type ?? ""}
        style={error ? { border: " var(--error-color) solid 1px" } : {}}
        placeholder={placeholder}
      />
      {error && <div className="sign-up-pop-up-input-error">{error}</div>}
    </div>
  );
};
